import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import SectionEditor from "@/components/cms/SectionEditor";
import { SortableList } from "@/components/cms/SortableList";
import VersionHistoryDrawer from "@/components/cms/VersionHistoryDrawer";
import { ADMIN } from "@/constants/testIds";
import Logo from "@/components/Logo";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const uid = () => Math.random().toString(36).slice(2, 10);

const emptySection = () => ({
  _uid: uid(),
  key: `section-${uid().slice(0, 6)}`,
  title: "New section",
  order: 0,
  blocks: [],
});

// Assign stable UIDs to sections + blocks (only used inside the editor).
function withUids(page) {
  return {
    ...page,
    sections: (page.sections || []).map((s) => ({
      ...s,
      _uid: s._uid || uid(),
      blocks: (s.blocks || []).map((b) => ({ ...b, _uid: b._uid || uid() })),
    })),
  };
}

function stripUids(page) {
  return {
    slug: page.slug,
    title: page.title,
    seo_title: page.seo_title || "",
    seo_description: page.seo_description || "",
    published: !!page.published,
    sections: (page.sections || []).map((s, i) => ({
      key: s.key, title: s.title || "", order: i,
      blocks: (s.blocks || []).map((b, j) => ({
        key: b.key, type: b.type, data: b.data || {}, order: j,
      })),
    })),
  };
}

function duplicateSectionValue(sec) {
  const suffix = `-copy-${uid().slice(0, 4)}`;
  return {
    ...sec,
    _uid: uid(),
    key: `${sec.key}${suffix}`.slice(0, 60),
    title: `${sec.title || "Section"} (copy)`,
    blocks: (sec.blocks || []).map((b) => ({
      ...b,
      _uid: uid(),
      key: `${b.key}${suffix}`.slice(0, 60),
      data: JSON.parse(JSON.stringify(b.data || {})),
    })),
  };
}

export default function AdminCmsPage() {
  const [pages, setPages] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = () => {
    logout();
    nav("/admin");
  };

  const loadPages = useCallback(async () => {
    const { data } = await api.get("/admin/cms/pages");
    setPages(data);
    return data;
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);

  const openPage = async (slug) => {
    setSelectedSlug(slug);
    const p = pages.find((x) => x.slug === slug);
    if (p) setDraft(withUids(JSON.parse(JSON.stringify(p))));
  };

  const patch = (updates) => setDraft((d) => ({ ...d, ...updates }));

  const updateSection = (idx, next) => {
    const s = [...draft.sections];
    s[idx] = next;
    patch({ sections: s });
  };
  const addSection = () => {
    patch({ sections: [...(draft.sections || []), emptySection()] });
  };
  const removeSection = (idx) => {
    if (!confirm("Delete this section and all its blocks?")) return;
    patch({ sections: draft.sections.filter((_, i) => i !== idx) });
  };
  const duplicateSection = (idx) => {
    const orig = draft.sections[idx];
    const copy = duplicateSectionValue(orig);
    const arr = [...draft.sections];
    arr.splice(idx + 1, 0, copy);
    patch({ sections: arr });
    toast.success(`Section "${orig.title}" duplicated.`);
  };
  const reorderSections = (newList) => patch({ sections: newList });

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = stripUids(draft);
      await api.put(`/admin/cms/pages/${draft.slug}`, payload);
      toast.success("Page saved.");
      const fresh = await loadPages();
      const upd = fresh.find((p) => p.slug === draft.slug);
      if (upd) setDraft(withUids(JSON.parse(JSON.stringify(upd))));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };


  const exportAll = async () => {
    setExporting(true);

    try {
      const { data } = await api.post("/admin/cms/export-all");

      toast.success(data.message);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to export all pages."
      );
    } finally {
      setExporting(false);
    }
  };

  const del = async (slug) => {
    if (!confirm(`Delete the page "${slug}" permanently?`)) return;
    try {
      await api.delete(`/admin/cms/pages/${slug}`);
      toast.success("Page deleted.");
      if (selectedSlug === slug) { setSelectedSlug(null); setDraft(null); }
      await loadPages();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const createPage = async () => {
    if (!/^[a-z0-9-]+$/.test(newSlug)) return toast.error("Slug must be lowercase letters, numbers, hyphens.");
    if (!newTitle.trim()) return toast.error("Title required.");
    try {
      await api.post("/admin/cms/pages", {
        slug: newSlug, title: newTitle.trim(),
        seo_title: "", seo_description: "", published: true, sections: [],
      });
      toast.success("Page created.");
      setCreating(false); setNewSlug(""); setNewTitle("");
      await loadPages();
      await openPage(newSlug);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (!pages) return <LoadingScreen />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="admin-cms">
      {/* Sidebar: page list */}
      <div
        className="lg:col-span-1 sqit-card sticky top-0 h-[calc(100vh-7rem)] flex flex-col p-4"
        data-testid={ADMIN.cmsPageList}
      >

        <div className="flex justify-center border-b border-gray-200 pb-4 mb-4">
          <Logo className="justify-center" />
        </div>

        <button
          onClick={() => setCreating(true)}
          data-testid="admin-cms-new-page-btn"
          className="w-full mb-4 bg-[#006B3C] text-white hover:bg-[#09582D] rounded-md px-3 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1.5"
        >
          <Icons.Plus size={14} /> New page
        </button>
        <button
          onClick={exportAll}
          disabled={exporting}
          className="w-full mb-4 border border-[#006B3C] text-[#006B3C] hover:bg-[#EAF4E3] rounded-md px-3 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
        >
          <Icons.UploadCloud size={14} />
          {exporting ? "Exporting..." : "Export All"}
        </button>
        {creating && (
          <div className="mb-4 p-3 border border-[#E5E7EB] rounded-lg bg-[#F8FAF7]">
            <input
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              placeholder="page-slug"
              data-testid="admin-cms-new-slug"
              className="w-full mb-2 border border-gray-300 rounded-md px-2 py-1.5 text-sm font-mono"
            />
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Page title"
              data-testid="admin-cms-new-title"
              className="w-full mb-2 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
            <div className="flex gap-1.5">
              <button onClick={createPage}
                data-testid="admin-cms-new-create"
                className="flex-1 bg-[#006B3C] text-white rounded-md px-2 py-1.5 text-xs font-semibold">Create</button>
              <button onClick={() => setCreating(false)} className="px-2 py-1.5 border rounded-md text-xs">Cancel</button>
            </div>
          </div>
        )}
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
          Pages
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {pages.map((p) => (
            <div key={p.slug}
              className={`group flex items-center rounded-md ${selectedSlug === p.slug ? "bg-[#EAF4E3]" : "hover:bg-gray-50"
                }`}
            >
              <button
                onClick={() => openPage(p.slug)}
                data-testid={`admin-cms-page-${p.slug}`}
                className="flex-1 text-left px-3 py-2 min-w-0"
              >
                <div className={`text-sm font-medium truncate ${selectedSlug === p.slug ? "text-[#09582D]" : "text-gray-900"}`}>
                  {p.title}
                </div>
                <div className="text-[11px] text-gray-500 font-mono">/{p.slug}</div>
              </button>
              <button onClick={() => del(p.slug)}
                data-testid={`admin-cms-delete-${p.slug}`}
                className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-[#DC2626] transition-opacity"
                title="Delete page"
              ><Icons.Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-200 pt-4 mt-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 rounded-md border border-red-300 text-red-600 hover:bg-red-50 px-3 py-2 text-sm font-medium transition-colors"
          >
            <Icons.LogOut size={16} />
            Logout
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="lg:col-span-3">
        {draft ? (
          <div className="space-y-6">
            {/* Sticky page toolbar */}
            <div className="sqit-card p-5 top-24 z-20">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex-1">
                  <input
                    data-testid="admin-cms-page-title"
                    value={draft.title || ""}
                    onChange={(e) => patch({ title: e.target.value })}
                    className="w-full font-display text-xl font-semibold text-gray-900 border-none focus:ring-0 outline-none bg-transparent"
                    placeholder="Page title"
                  />
                  <div className="text-xs text-gray-500 font-mono mt-0.5">/{draft.slug}</div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox" checked={!!draft.published}
                      data-testid="admin-cms-published"
                      onChange={(e) => patch({ published: e.target.checked })}
                    />
                    Published
                  </label>
                  <button
                    onClick={() => setHistoryOpen(true)}
                    data-testid="admin-cms-history-btn"
                    className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#09582D] border border-gray-300 rounded-md px-3 py-2"
                    title="Version history"
                  >
                    <Icons.History size={14} /> History
                  </button>
                  <a
                    href={draft.slug === "home" ? "/" : `/${draft.slug}`}
                    target="_blank" rel="noreferrer"
                    className="text-sm text-gray-600 hover:text-[#09582D] inline-flex items-center gap-1 border border-gray-300 rounded-md px-3 py-2"
                  >
                    <Icons.ExternalLink size={12} /> Preview
                  </a>
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid={ADMIN.cmsSaveBtn}
                    className="bg-[#006B3C] text-white hover:bg-[#09582D] transition-colors rounded-md px-5 py-2 text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5"
                  >
                    <Icons.Save size={14} />
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
              {/* SEO */}
              <div className="mt-4 pt-4 border-t border-[#E5E7EB] grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">SEO title</label>
                  <input
                    data-testid="admin-cms-seo-title"
                    value={draft.seo_title || ""}
                    onChange={(e) => patch({ seo_title: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#006B3C]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">SEO description</label>
                  <input
                    data-testid="admin-cms-seo-description"
                    value={draft.seo_description || ""}
                    onChange={(e) => patch({ seo_description: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#006B3C]"
                  />
                </div>
              </div>
            </div>

            {/* Sections — sortable via drag & drop */}
            <div className="space-y-4">
              <SortableList
                items={draft.sections || []}
                idKey="_uid"
                onReorder={reorderSections}
                renderItem={(section, { dragHandleProps }) => {
                  const i = draft.sections.findIndex((s) => s._uid === section._uid);
                  return (
                    <SectionEditor
                      section={section}
                      sectionIndex={i}
                      totalSections={draft.sections.length}
                      dragHandleProps={dragHandleProps}
                      onChange={(next) => updateSection(i, { ...next, _uid: section._uid })}
                      onRemove={() => removeSection(i)}
                      onDuplicate={() => duplicateSection(i)}
                    />
                  );
                }}
              />
              <button
                onClick={addSection}
                data-testid="admin-cms-add-section"
                className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:border-[#006B3C] hover:text-[#09582D] hover:bg-[#EAF4E3]/30 transition-colors"
              >
                + Add section
              </button>
            </div>
          </div>
        ) : (
          <div className="sqit-card p-14 text-center text-gray-500">
            <Icons.LayoutTemplate size={32} className="mx-auto text-gray-300" />
            <p className="mt-4 text-sm">Select a page on the left to edit, or create a new one.</p>
            <p className="mt-1 text-xs">Pages → Sections → Blocks → Fields. No JSON required.</p>
          </div>
        )}
      </div>
      {draft && (
        <VersionHistoryDrawer
          slug={draft.slug}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onRestored={async () => {
            const fresh = await loadPages();
            const p = fresh.find((x) => x.slug === draft.slug);
            if (p) setDraft(withUids(JSON.parse(JSON.stringify(p))));
          }}
        />
      )}
    </div>
  );
}
